import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { LOCAL_STORAGE_KEYS } from '@/constants/storage';
import { useNavigate } from 'react-router-dom';
import { langPath } from '@/utils/langPath';
import { setAuthContext } from './global/globalAuthRef';
import { ROLE_TYPE } from '@/api/type/commonTypes';
import { ClientConfig } from '@/libs/r2-service-client';

export interface AuthContextType {
  isLoggedIn: boolean;
  roleType: ROLE_TYPE;
  login: (token: string, role: ROLE_TYPE) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isLoggedIn: false,
  roleType: 'CENTER',
  login: () => {},
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);
const storedRole = localStorage.getItem(LOCAL_STORAGE_KEYS.ROLE_TYPE);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN)
  );
  const [roleType, setRoleType] = useState<ROLE_TYPE>(() => {
    const storedRole = localStorage.getItem(
      LOCAL_STORAGE_KEYS.ROLE_TYPE
    ) as ROLE_TYPE | null;

    return storedRole ?? 'PLAYER';
  });
  const navigate = useNavigate();
  const isLoggedIn = !!token;

  const login = useCallback((token: string, role: ROLE_TYPE) => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, token);
    localStorage.setItem(LOCAL_STORAGE_KEYS.ROLE_TYPE, role);
    setToken(token);
    setRoleType(role);

    // 로그인 이후, 토큰이 설정되도록 함
    ClientConfig.instance.setToken(token);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.ROLE_TYPE);
    setToken(null);
    navigate(langPath('/login'), { replace: true });
  }, [navigate]);

  useEffect(() => {
    setAuthContext({ isLoggedIn, roleType, login, logout });
  }, [isLoggedIn, login, logout, roleType]);

  return (
    <AuthContext.Provider value={{ isLoggedIn, roleType, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
